/**
 * App.tsx
 *
 * File purpose:
 * - App main router container. Registers routes and top-level layout.
 * - Uses HashRouter because this environment has only `react-router` installed.
 */

/**
 * Import react-router and app pages
 */
import { BrowserRouter, Route, Routes } from 'react-router'
import HomePage from './pages/Home'
import AboutPage from './pages/About'
import ServicesPage from './pages/Services'
import DigitalMarketingPage from './pages/services/DigitalMarketing'
import MediaPlanningPage from './pages/services/MediaPlanning'
import InfluencerMarketingPage from './pages/services/InfluencerMarketing'
import PaidAdvertisingPage from './pages/services/PaidAdvertising'
import SocialMediaPage from './pages/services/SocialMedia'
import ContentCreationPage from './pages/services/ContentCreation'
import TranslationPage from './pages/services/Translation'
import TVAdvertisingPage from './pages/services/TVAdvertising'
import OOHAdvertisingPage from './pages/services/OOHAdvertising'
import RadioAdvertisingPage from './pages/services/RadioAdvertising'
import PrintAdvertisingPage from './pages/services/PrintAdvertising'
import MonitoringPage from './pages/services/Monitoring'
import DubbingPage from './pages/services/Dubbing'
import PortfolioPage from './pages/Portfolio'
import ContactPage from './pages/Contact'
import Layout from './components/Layout'
import TeamPage from './pages/Team'
import ClientWorkDetails from './pages/ClientWorkDetails'
import TermsPage from './pages/Terms'
import PrivacyPage from './pages/Privacy'
import ScrollToTop from './components/ScrollToTop'

/**
 * Local landing pages
 */
import ErbilLanding from './pages/landings/Erbil'
import BaghdadLanding from './pages/landings/Baghdad'
import KurdistanLanding from './pages/landings/Kurdistan'

/**
 * DeFacto portfolio page (explicit route)
 * - Ensure the dedicated DeFacto page is reachable at /portfolio/defacto
 */
import DeFactoPage from './pages/portfolio/defacto'

/**
 * App component
 *
 * Purpose:
 * - Central router for the application using HashRouter (safe for this environment).
 */
/**
 * App component
 *
 * Renders the router, ScrollToTop, Layout wrapper and all route definitions.
 */
export default function App() {
  return (
    <BrowserRouter>
      {/* Keep ScrollToTop inside the Router so it responds to navigation changes */}
      <ScrollToTop />
      <Layout>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/digital-marketing" element={<DigitalMarketingPage />} />
          <Route path="/services/media-planning" element={<MediaPlanningPage />} />
          <Route path="/services/influencer-marketing" element={<InfluencerMarketingPage />} />
          <Route path="/services/paid-advertising" element={<PaidAdvertisingPage />} />
          <Route path="/services/social-media" element={<SocialMediaPage />} />
          <Route path="/services/content-creation" element={<ContentCreationPage />} />
          <Route path="/services/translation" element={<TranslationPage />} />
          <Route path="/services/tv-advertising" element={<TVAdvertisingPage />} />
          <Route path="/services/ooh-advertising" element={<OOHAdvertisingPage />} />
          <Route path="/services/radio-advertising" element={<RadioAdvertisingPage />} />
          <Route path="/services/print-advertising" element={<PrintAdvertisingPage />} />
          <Route path="/services/monitoring" element={<MonitoringPage />} />
          <Route path="/services/dubbing" element={<DubbingPage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/portfolio" element={<PortfolioPage />} />
          <Route path="/portfolio/:clientId" element={<ClientWorkDetails />} />

          {/* Explicit DeFacto page route */}
          <Route path="/portfolio/defacto" element={<DeFactoPage />} />

          <Route path="/contact" element={<ContactPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />

          {/* Local landing pages routes */}
          <Route path="/landings/erbil" element={<ErbilLanding />} />
          <Route path="/landings/baghdad" element={<BaghdadLanding />} />
          <Route path="/landings/kurdistan" element={<KurdistanLanding />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  )
}
